import { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from 'react';

interface AuthState {
  userId: string | null;
  sessionJwt: string | null;
}

type AuthAction =
  | { type: 'login'; userId: string; sessionJwt: string }
  | { type: 'logout' };

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'login': return { userId: action.userId, sessionJwt: action.sessionJwt };
    case 'logout': return { userId: null, sessionJwt: null };
  }
}

interface AuthContextValue {
  userId: string | null;
  isAuthed: boolean;
  getJwt: () => string | null;
  login: (userId: string, sessionJwt: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'relay.auth';

function loadFromStorage(): AuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { userId: null, sessionJwt: null };
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (typeof parsed.userId === 'string' && typeof parsed.sessionJwt === 'string') {
      return { userId: parsed.userId, sessionJwt: parsed.sessionJwt };
    }
  } catch { /* ignore */ }
  return { userId: null, sessionJwt: null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadFromStorage);

  const getJwt = useCallback(() => state.sessionJwt, [state.sessionJwt]);
  const login = useCallback((userId: string, sessionJwt: string) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, sessionJwt })); } catch { /* ignore */ }
    dispatch({ type: 'login', userId, sessionJwt });
  }, []);
  const logout = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    dispatch({ type: 'logout' });
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    userId: state.userId,
    isAuthed: state.sessionJwt !== null,
    getJwt,
    login,
    logout,
  }), [state.userId, state.sessionJwt, getJwt, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
